import { Dispatch, SetStateAction, useState } from 'react'
import { Button, Dialog, Stack, Typography } from '@mui/material'
import { OpenTaskDeleteModal, Task, TaskLogPayload } from '../types'
import CloseModalButton from '../../../../components/CloseModalButton'
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from '../../../../components/Dialog.styled'
import { useAppDispatch, useAppSelector } from '../../../../redux/store'
import { getPracticeTasks } from '../slice'
import { selectCurrentUser, selectIsAdminOrOwner } from '../../../../redux/user'
import * as service from '../service'
import useCustomerNumber from '../../../../hooks/useCustomerNumber'
import { addError, addSuccess } from '../../../../redux/alerts'
import { getIsOneTimeTask, getTaskLogPayload } from '../utils/helpers'
import { TaskLogStatus } from '../enums'

interface DeleteTaskModalProps {
  task: Task
  open: boolean
  setOpenDeleteTaskModal: Dispatch<SetStateAction<OpenTaskDeleteModal>>
}

const buttonStyles = {
  backgroundColor: '#f05050',
  '&:hover': { backgroundColor: '#ee3939' },
}

const DeleteTaskModal = (props: DeleteTaskModalProps) => {
  const { open, task, setOpenDeleteTaskModal } = props

  const customerNumber = useCustomerNumber()

  const dispatch = useAppDispatch()

  const currentUser = useAppSelector(selectCurrentUser)
  const isAdminOrOwner = useAppSelector(selectIsAdminOrOwner)

  const [isLoading, setIsLoading] = useState(false)

  const handleClose = () => {
    setIsLoading(false)
    setOpenDeleteTaskModal({ open: false, task: null })
  }

  const handleAction = async (action: 'delete' | 'skip', task: Task) => {
    try {
      setIsLoading(true)
      
      const isOneTime = getIsOneTimeTask(task)

      if (action === 'skip' && currentUser) {
        const payload: TaskLogPayload = getTaskLogPayload(task, currentUser, TaskLogStatus.Skipped)
        await service.createTaskLog(customerNumber, payload)
      }

      if (action === 'delete' || isOneTime) {
        await service.deleteTask(customerNumber, task)
      }

      await dispatch(getPracticeTasks(customerNumber))

      const message = `Your task has successfully been ${action === 'skip' ? 'skipped' : 'deleted'}.`

      dispatch(addSuccess(message))
    } catch (error) {
      const message = `Failed to ${action} task. Please try again.`
      dispatch(addError(message))
    } finally {
      handleClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <CloseModalButton handleClose={handleClose} />
      <StyledDialogTitle>Delete Task</StyledDialogTitle>
      <StyledDialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography>Are you sure you want to delete this task?</Typography>
        </Stack>
      </StyledDialogContent>
      <StyledDialogFooter>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        {isAdminOrOwner && (task?.cron || task?.weekly) && (
          <Button sx={buttonStyles} disabled={isLoading} variant="contained" onClick={() => handleAction('delete', task)}>
            Delete all future occurrences
          </Button>
        )}
        <Button sx={buttonStyles} disabled={isLoading} variant="contained" onClick={() => handleAction('skip', task)}>
          Skip next occurrence
        </Button>
      </StyledDialogFooter>
    </Dialog>
  )
}
export default DeleteTaskModal